import * as React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {allTimezones, useTimezoneSelect} from "react-timezone-select";
import {ErrorMessage, useField, useFormikContext} from "formik";
import {formatISO, parseISO} from "../../helper/date";
import {DateTime} from "luxon";

export enum AutomaticTime {
    OFF = "off",
    STARTOFDAY = "startofday",
    ENDOFDAY = "endofday",
}

interface DatePickerFieldProps {
    name: string;
    dateFormat?: string;
    automaticTime?: AutomaticTime;
    timeIntervals?: number;
    showTimeSelect?: boolean;
    showTimeZoneSelect?: boolean;

    [key: string]: any;
}

interface TimezoneSelectProps {
    value: string;
    onChange: (timezone: string) => void;

    [key: string]: any;
}

/**
 * Utility function to get a DateTime object based on the provided date string and automatic time setting.
 * If automaticTime is STARTOFDAY, it sets the time to the start of the
 * day (00:00:00).
 * If automaticTime is ENDOFDAY, it sets the time to the end of the
 * day (23:59:59).
 *
 * @param date - The date string to parse.
 * @param automaticTime - The automatic time setting.
 * @returns A DateTime object representing the date with the specified time adjustment.
 */
const getDateTime = (date: string, automaticTime?: AutomaticTime): DateTime | null => {
    if (!date || date.trim() === '') {
        return null;
    }
    let newDate = parseISO(date);
    if (!newDate.isValid) {
        return null;
    }
    if (automaticTime && automaticTime === AutomaticTime.STARTOFDAY) {
        newDate = newDate.startOf("day");
    }
    if (automaticTime && automaticTime === AutomaticTime.ENDOFDAY) {
        newDate = newDate.endOf("day");
    }
    return newDate;
};

const TimezoneSelect: React.FC<TimezoneSelectProps> = ({value, onChange}) => {
    const labelStyle = "original";
    const {options, parseTimezone} = useTimezoneSelect({
        labelStyle,
        timezones: {
            ...allTimezones,
            "Europe/Berlin": "Berlin",
        },
    });

    return (
        <React.Fragment>
            <label className="text-sm font-medium mt-4 border-top-1 border-gray-300">
                Timezone:
            </label>
            <select
                value={value}
                onChange={(e) => {
                    const newTimezone = parseTimezone(e.target.value);
                    return onChange(newTimezone.value);
                }}
                className="block w-full shadow-xs sm:text-sm border-gray-300 rounded-md"
            >
                {options.map((option) => (
                    <option value={option.value} key={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </React.Fragment>
    );
};

const DatePickerField: React.FC<DatePickerFieldProps> = ({
                                                             name,
                                                             dateFormat = "dd.MM.yyyy HH:mm:ss",
                                                             timeIntervals = 1,
                                                             automaticTime = AutomaticTime.OFF,
                                                             timezones = allTimezones,
                                                             defaultTimezone = "Europe/Berlin",
                                                             timeFormat = "HH:mm",
                                                             calendarStartDay = 1,
                                                             showTimeZoneSelect = false,
                                                             ...props
                                                         }) => {
    const {setFieldValue} = useFormikContext<any>();
    const [field] = useField(name);
    const [timezone, setTimezone] = React.useState<string>(defaultTimezone);
    const [selectedDate, setSelectedDate] = React.useState<Date | null>(() => {
        if (!field.value) {
            return null;
        }
        const dateTime = getDateTime(field.value, automaticTime);
        return dateTime ? dateTime.toJSDate() : null;
    });

    const handleTimezoneChange = (newTimezone: string) => {
        setTimezone(newTimezone);
        if (!field.value) {
            return;
        }

        const dateTime = parseISO(field.value);
        const formattedDate = formatISO(dateTime, newTimezone);
        setFieldValue(name, formattedDate);
        setSelectedDate(dateTime.setZone(newTimezone).toJSDate());
    };

    return (
        <div className={props.className ? props.className : ""}>
            <DatePicker
                {...props}
                selected={selectedDate}
                onChange={(date: Date) => {
                    const formattedDate = date ? formatISO(date, timezone) : "";
                    const dateTime = getDateTime(formattedDate, automaticTime);
                    const newDate = dateTime ? dateTime.setZone(timezone).toJSDate() : null;
                    setFieldValue(name, formattedDate);
                    setSelectedDate(newDate);
                }}
                showIcon
                showTimeSelect={props.showTimeSelect || false}
                timeIntervals={timeIntervals}
                dateFormat={dateFormat}
                timeFormat={timeFormat}
                calendarStartDay={calendarStartDay}
                closeOnScroll={true}
                className="block w-full min-w-48 shadow-xs sm:text-sm focus:ring-red-500 focus:border-red-500 border-gray-300 rounded-md"
            >
                {showTimeZoneSelect && (
                    <TimezoneSelect
                        value={timezone}
                        onChange={handleTimezoneChange}
                    />
                )}
            </DatePicker>
            <ErrorMessage
                name={name}
                component="p"
                className="mt-2 text-sm text-red-600"
            />
        </div>
    );
};

export default DatePickerField;
